
import { AuthProvider } from "@/contexts/AuthContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { Routes, Route, Navigate } from "react-router-dom";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import SchoolsOverview from "@/pages/SchoolsOverview";
import AcademicYears from "@/pages/AcademicYears";
import ClassesAndStreams from "@/pages/ClassesAndStreams";
import NurseDashboard from "@/pages/nurse/NurseDashboard";
import Students from "@/pages/nurse/Students";
import Settings from "@/pages/nurse/Settings";
import Referrals from "@/pages/nurse/Referrals";
import Reports from "@/pages/nurse/Reports";
import Dispensary from "@/pages/nurse/Dispensary";
import Visits from "@/pages/nurse/Visits";
import History from "@/pages/nurse/History";
import SpecialMonitoring from "@/pages/nurse/SpecialMonitoring";
import ProtectedRoute from "@/components/ProtectedRoute";
import { ConnectionProvider } from "@/contexts/ConnectionContext";
import { OfflineProvider } from "@/contexts/OfflineContext";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (error instanceof Error && error.message?.includes('404')) {
          return false;
        }
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 30000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: true,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ConnectionProvider>
        <OfflineProvider>
          <AuthProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute allowedRoles={["SUPER_ADMIN"]}>
                    <Navigate to="/user-management" replace />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/user-management"
                element={
                  <ProtectedRoute allowedRoles={["SUPER_ADMIN"]}>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/schools-overview"
                element={
                  <ProtectedRoute allowedRoles={["SUPER_ADMIN"]}>
                    <SchoolsOverview />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/academic-years"
                element={
                  <ProtectedRoute allowedRoles={["SUPER_ADMIN"]}>
                    <AcademicYears />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/classes-and-streams"
                element={
                  <ProtectedRoute allowedRoles={["SUPER_ADMIN"]}>
                    <ClassesAndStreams />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nurse-dashboard"
                element={
                  <ProtectedRoute allowedRoles={["NURSE"]}>
                    <NurseDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nurse/students"
                element={
                  <ProtectedRoute allowedRoles={["NURSE"]}>
                    <Students />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nurse/settings"
                element={
                  <ProtectedRoute allowedRoles={["NURSE"]}>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nurse/referrals"
                element={
                  <ProtectedRoute allowedRoles={["NURSE"]}>
                    <Referrals />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nurse/reports"
                element={
                  <ProtectedRoute allowedRoles={["NURSE"]}>
                    <Reports />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nurse/visits"
                element={
                  <ProtectedRoute allowedRoles={["NURSE"]}>
                    <Visits />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nurse/history"
                element={
                  <ProtectedRoute allowedRoles={["NURSE"]}>
                    <History />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nurse/dispensary"
                element={
                  <ProtectedRoute allowedRoles={["NURSE"]}>
                    <Dispensary />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nurse/special-monitoring"
                element={
                  <ProtectedRoute allowedRoles={["NURSE"]}>
                    <SpecialMonitoring />
                  </ProtectedRoute>
                }
              />
            </Routes>
            <Toaster />
          </AuthProvider>
        </OfflineProvider>
      </ConnectionProvider>
    </QueryClientProvider>
  );
}

export default App;
